import { useState } from "react";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { fmtErr } from "@/lib/api";

export default function WorkHistoryEditor() {
  const { user, setUser } = useAuth();
  const [items, setItems] = useState(user?.profile?.work_history || []);
  const [saving, setSaving] = useState(false);

  const setRow = (i, k, v) => setItems(items.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const add = () => setItems([...items, { client: "", role: "", year: "" }]);
  const remove = (i) => setItems(items.filter((_, j) => j !== i));

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = items.filter((r) => (r.client || "").trim() || (r.role || "").trim());
      const { data } = await api.put("/profile", { work_history: cleaned });
      setUser(data);
      setItems(cleaned);
      toast.success("Work history updated.");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="panel p-8 mt-10" data-testid="work-history-editor">
      <div className="flex items-center justify-between mb-8">
        <p className="overline-label">Selected work ({items.length})</p>
        <button data-testid="add-credit-button" onClick={add} className="btn-outline !px-5 !py-2.5">
          <Plus size={14} /> Add credit
        </button>
      </div>
      {items.length === 0 ? (
        <p className="font-display italic text-2xl text-white/40" data-testid="credits-empty">
          No credits yet — every legend starts somewhere.
        </p>
      ) : (
        <div className="flex flex-col gap-6">
          {items.map((w, i) => (
            <div key={i} className="grid grid-cols-12 gap-4 items-end" data-testid={`credit-row-${i}`}>
              <div className="col-span-12 sm:col-span-4">
                <label className="overline-label !text-[10px] block mb-1">Client / house</label>
                <input data-testid={`credit-client-${i}`} value={w.client || ""} onChange={(e) => setRow(i, "client", e.target.value)} className="field" placeholder="Maison Lumière" />
              </div>
              <div className="col-span-9 sm:col-span-5">
                <label className="overline-label !text-[10px] block mb-1">Role / job</label>
                <input data-testid={`credit-role-${i}`} value={w.role || ""} onChange={(e) => setRow(i, "role", e.target.value)} className="field" placeholder="SS27 Runway — opener" />
              </div>
              <div className="col-span-3 sm:col-span-2">
                <label className="overline-label !text-[10px] block mb-1">Year</label>
                <input data-testid={`credit-year-${i}`} value={w.year || ""} onChange={(e) => setRow(i, "year", e.target.value)} className="field" placeholder="2026" />
              </div>
              <div className="col-span-12 sm:col-span-1 flex sm:justify-end">
                <button data-testid={`remove-credit-${i}`} onClick={() => remove(i)} className="font-grotesk text-[10px] uppercase tracking-[0.15em] border border-white/25 hover:border-accent hover:text-accent px-3 py-2 transition-colors">
                  <X size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      <button data-testid="save-credits-button" onClick={save} disabled={saving} className="btn-primary mt-10 disabled:opacity-40">
        {saving ? "Saving…" : "Save credits"}
      </button>
    </div>
  );
}
